import { Component, lazy, Suspense, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ListingDetailContent from "@/components/yts/ListingDetailContent";
import LoopExperience from "@/pages/LoopExperience";
import { useExplore } from "@/lib/ExploreContext";
import ViatorDetailContent from "@/components/yts/ViatorDetailContent";
import DetailOverlayHeader from "@/components/yts/DetailOverlayHeader";
import { VIATOR_SOURCE } from "@/lib/viator";
import { useDetailOverlay } from "@/lib/DetailOverlayContext";
import { trackGuideAction, captureGuideClick } from "@/lib/guideJourney";
const HotelDetail = lazy(() => import("@/pages/HotelDetail"));
const AutoDetail = lazy(() => import("@/pages/AutoDetail"));

/**
 * Full-screen detail view that opens over the current page — no navigation, so
 * the site header, the page beneath and the scroll position all stay put.
 * Closing returns the visitor exactly where they were.
 *
 * One card for everything you can open from a card or a pin: a local place, a
 * beach and a bookable partner experience all read in the same frame.
 */
class DetailErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Listing detail overlay failed:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-full bg-[#f7f6f2] grid place-items-center px-6 py-20 text-center">
          <div className="max-w-sm">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-[#777773]">Something went wrong</p>
            <h2 className="mt-2 text-[24px] font-black tracking-[-0.04em] text-[#171717]">This place could not be loaded.</h2>
            <p className="mt-2 text-[14px] leading-6 text-[#6d6d6a]">Close this panel and try the information button again.</p>
            <button
              type="button"
              onClick={() => this.setState({ hasError: false })}
              className="mt-5 h-11 px-5 rounded-[10px] bg-[#171717] text-white text-[12px] font-bold uppercase tracking-[0.12em]"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ListingDetailOverlay() {
  const { openId, openSource, close } = useDetailOverlay();
  const { allListings } = useExplore();
  const loopListing = openId === "6aba9f4aafdb8a3f19f131f3"
    ? allListings.find((listing) => listing.id === openId)
    : null;
  const marketplace = openSource === "hotel" || openSource === "auto";
  const closeRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (openId && !marketplace) trackGuideAction("guide_business_profile_view", { section: openSource === "viator" ? "experience" : "local" });
  }, [openId, openSource, marketplace]);

  useEffect(() => {
    if (!openId) {
      triggerRef.current?.focus?.();
      triggerRef.current = null;
      return;
    }
    if (!triggerRef.current) triggerRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      const items = Array.from(focusable).filter((el) => el.getAttribute("aria-hidden") !== "true" && el.getClientRects().length > 0);
      if (!items.length) {
        e.preventDefault();
        closeRef.current?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [openId, close]);

  return (
    <AnimatePresence>
      {openId && (
        <motion.div
          key="detail-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Place information"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={close}
          className={marketplace ? "fixed inset-x-0 top-[68px] bottom-[62px] z-[49] flex items-end justify-center bg-[#171717]/45 p-0 sm:top-[76px] sm:bottom-0 sm:items-center sm:bg-[#171717]/55 sm:p-5" : "fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-[#171717]/45 sm:bg-[#171717]/55 p-0 sm:p-5"}
        >
          <motion.div
            ref={panelRef}
            onClickCapture={captureGuideClick}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 26, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 18, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className={marketplace ? "relative flex h-full max-h-full w-full min-w-0 flex-col overflow-hidden rounded-t-[20px] border border-black/[0.12] bg-[#f7f6f2] shadow-[0_30px_100px_rgba(0,0,0,0.38)] sm:h-[min(840px,90dvh)] sm:max-h-full sm:max-w-[920px] sm:rounded-[18px]" : "relative z-[10000] flex h-[92dvh] max-h-[92dvh] w-full min-w-0 flex-col overflow-hidden rounded-t-[20px] border border-black/[0.12] bg-[#f7f6f2] shadow-[0_30px_100px_rgba(0,0,0,0.38)] sm:h-[min(880px,92dvh)] sm:max-h-[92dvh] sm:max-w-[900px] sm:rounded-[18px]"}
          >
            {/* The card carries the place's own name, plus close */}
            <DetailOverlayHeader openId={openId} openSource={openSource} closeRef={closeRef} onClose={close} />

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-[#f7f6f2]">
              <DetailErrorBoundary key={openId}>
                <Suspense fallback={<div role="status" className="p-8 text-sm text-[#77736c]">Loading details…</div>}>
                  {openSource === "hotel" ? <HotelDetail id={openId} embedded /> : openSource === "auto" ? <AutoDetail id={openId} embedded /> : openSource === VIATOR_SOURCE ? (
                    <ViatorDetailContent id={openId} overlay />
                  ) : loopListing ? (
                    <LoopExperience listing={loopListing} listings={allListings} />
                  ) : (
                    <ListingDetailContent id={openId} hideMap />
                  )}
                </Suspense>
              </DetailErrorBoundary>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}